import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Warehouse } from '@/interfaces/warehouse/Warehouse'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'

import {
  StockTransferFormValues,
  StockTransferFormSchema,
} from './schema'

interface StockTransferFormProps {
  isOpen: boolean
  onClose: () => void
  warehouses: Warehouse[]
  defaultFromWarehouseId?: string
  defaultSkuId?: string
  defaultBatchNumber?: string | null
  onSuccess?: () => void
}

export const StockTransferForm = ({
  isOpen,
  onClose,
  warehouses,
  defaultFromWarehouseId,
  defaultSkuId,
  defaultBatchNumber,
  onSuccess,
}: StockTransferFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'warehouse.stockTransfers',
  })

  const methods = useForm<StockTransferFormValues>({
    resolver: zodResolver(StockTransferFormSchema),
    defaultValues: {
      skuId: defaultSkuId ?? '',
      fromWarehouseId: defaultFromWarehouseId ?? '',
      toWarehouseId: '',
      quantity: 1,
      batchNumber: defaultBatchNumber ?? '',
      note: '',
    },
  })

  useEffect(() => {
    if (isOpen) {
      methods.reset({
        skuId: defaultSkuId ?? '',
        fromWarehouseId: defaultFromWarehouseId ?? '',
        toWarehouseId: '',
        quantity: 1,
        batchNumber: defaultBatchNumber ?? '',
        note: '',
      })
    }
  }, [
    defaultBatchNumber,
    defaultFromWarehouseId,
    defaultSkuId,
    isOpen,
    methods,
  ])

  const onValid = async (values: StockTransferFormValues) => {
    try {
      await API.post('/api/v2/stock-transfers', {
        skuId: values.skuId,
        fromWarehouseId: values.fromWarehouseId,
        toWarehouseId: values.toWarehouseId,
        quantity: values.quantity,
        batchNumber: values.batchNumber || null,
        note: values.note || null,
      })

      toast.success(t('create.success'))
      onSuccess?.()
      onClose()
    } catch (error: unknown) {
      console.error('Stock transfer error:', error)
      toast.error(t('create.error'), {
        description: extractErrorMessage(error) || t('error.description'),
      })
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose()
    }
  }

  const destinationWarehouses = warehouses.filter(
    (warehouse) => warehouse.id !== methods.watch('fromWarehouseId'),
  )

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('create.title')}</DialogTitle>
          <DialogDescription>{t('create.description')}</DialogDescription>
        </DialogHeader>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onValid)}
            className="w-full space-y-4"
          >
            <FormField
              control={methods.control}
              name="fromWarehouseId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.fromWarehouse.label')}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('form.fromWarehouse.placeholder')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {warehouses.map((warehouse) => (
                        <SelectItem key={warehouse.id} value={warehouse.id!}>
                          {warehouse.name} ({warehouse.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={methods.control}
              name="toWarehouseId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.toWarehouse.label')}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('form.toWarehouse.placeholder')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {destinationWarehouses.map((warehouse) => (
                        <SelectItem key={warehouse.id} value={warehouse.id!}>
                          {warehouse.name} ({warehouse.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={methods.control}
              name="skuId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.skuId.label')}</FormLabel>
                  <FormControl>
                    <Input {...field} readOnly={!!defaultSkuId} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={methods.control}
              name="quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.quantity.label')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      step={1}
                      {...field}
                      onChange={(event) => field.onChange(event.target.valueAsNumber)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={methods.control}
              name="batchNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.batchNumber.label')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      readOnly={!!defaultBatchNumber}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={methods.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.note.label')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('form.note.placeholder')}
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('form.cancel')}
              </Button>
              <Button type="submit">
                {t('form.save')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
